import Vue from 'vue'
import VueRouter from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { auth, db } from '../firebase'
import { onAuthStateChanged } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'

Vue.use(VueRouter)

// meta.roles lists who may open a page. Admins can open Staff pages too.
const STAFF = ['Staff', 'Admin']
const ADMIN = ['Admin']
const CUSTOMER = ['Customer']

const routes = [
  // ---------- Public site ----------
  { path: '/', name: 'home', component: HomeView, meta: { title: 'Booking & business software for barbershops and salons' } },
  { path: '/why', name: 'why', component: () => import(/* webpackChunkName: "site" */ '../views/WhyView.vue'), meta: { title: 'Why Salon Zap' } },
  { path: '/features', name: 'features', component: () => import(/* webpackChunkName: "site" */ '../views/FeaturesView.vue'), meta: { title: 'Features' } },
  { path: '/tribzz', name: 'tribzz', component: () => import(/* webpackChunkName: "site" */ '../views/TribzzView.vue'), meta: { title: 'Tribzz for barbers & salons' } },
  { path: '/about', name: 'about', component: () => import(/* webpackChunkName: "site" */ '../views/AboutView.vue'), meta: { title: 'About' } },
  { path: '/demo', name: 'demo', component: () => import(/* webpackChunkName: "site" */ '../views/DemoView.vue'), meta: { title: 'Request a demo' } },
  { path: '/contact', name: 'contact', component: () => import(/* webpackChunkName: "site" */ '../views/ContactView.vue'), meta: { title: 'Contact' } },
  { path: '/blog', name: 'blog', component: () => import(/* webpackChunkName: "blog" */ '../views/BlogView.vue'), meta: { title: 'Blog' } },
  { path: '/blog/:slug', name: 'blog-post', component: () => import(/* webpackChunkName: "blog" */ '../views/BlogPostView.vue'), meta: { title: 'Blog' } },
  { path: '/documentation', name: 'documentation', component: () => import(/* webpackChunkName: "help" */ '../views/DocumentationView.vue'), meta: { title: 'Documentation' } },
  { path: '/help', name: 'help', component: () => import(/* webpackChunkName: "help" */ '../views/HelpView.vue'), meta: { title: 'Help Center' } },
  { path: '/security', name: 'security', component: () => import(/* webpackChunkName: "help" */ '../views/SecurityView.vue'), meta: { title: 'Security' } },
  { path: '/privacy', name: 'privacy', component: () => import(/* webpackChunkName: "legal" */ '../views/Privacy.vue'), meta: { title: 'Privacy Policy' } },
  { path: '/terms', name: 'terms', component: () => import(/* webpackChunkName: "legal" */ '../views/Terms.vue'), meta: { title: 'Terms of Service' } },
  { path: '/cookies', name: 'cookies', component: () => import(/* webpackChunkName: "legal" */ '../views/Cookies.vue'), meta: { title: 'Cookie Policy' } },
  { path: '/login', name: 'login', component: () => import(/* webpackChunkName: "auth" */ '../views/LoginView.vue'), meta: { title: 'Log in' } },
  { path: '/reset-password', name: 'reset-password', component: () => import(/* webpackChunkName: "auth" */ '../views/ResetPasswordView.vue'), meta: { title: 'Reset password' } },

  // ---------- Staff ----------
  { path: '/staff-dashboard', name: 'StaffDashboard', component: () => import(/* webpackChunkName: "staff" */ '../views/StaffDashboardView.vue'), meta: { roles: STAFF, title: 'Staff dashboard' } },
  { path: '/staff-onboard-customer', name: 'StaffOnboardCustomer', component: () => import(/* webpackChunkName: "staff-onboard" */ '../views/StaffOnboardCustomerView.vue'), meta: { roles: STAFF, title: 'Onboard a shop' } },
  { path: '/staff-leads', name: 'StaffLeads', component: () => import(/* webpackChunkName: "staff" */ '../views/StaffLeads.vue'), meta: { roles: STAFF, title: 'Leads' } },
  { path: '/staff-payments', name: 'StaffPayments', component: () => import(/* webpackChunkName: "staff" */ '../views/StaffPaymentsView.vue'), meta: { roles: STAFF, title: 'My payments' } },
  { path: '/staff/templates', name: 'staff-templates', component: () => import(/* webpackChunkName: "staff" */ '../views/StaffTemplates.vue'), meta: { roles: STAFF, title: 'Templates' } },
  { path: '/staff/blog', name: 'staff-blog', component: () => import(/* webpackChunkName: "staff-blog" */ '../views/StaffBlogManageView.vue'), meta: { roles: STAFF, title: 'Manage blog' } },
  { path: '/staff/products', name: 'StaffProducts', component: () => import(/* webpackChunkName: "staff-products" */ '../views/StaffProducts.vue'), meta: { roles: STAFF, title: 'Products' } },
  { path: '/staff/products/:id', name: 'StaffProductDetail', component: () => import(/* webpackChunkName: "staff-products" */ '../views/StaffProductDetail.vue'), props: true, meta: { roles: STAFF, title: 'Product' } },
  { path: '/staff-manage-settings', name: 'StaffManageSettings', component: () => import(/* webpackChunkName: "staff" */ '../views/StaffManageSettingsView.vue'), meta: { roles: STAFF, title: 'My settings' } },
  { path: '/manage-app-progress', name: 'ManageAppProgress', component: () => import(/* webpackChunkName: "staff" */ '../views/ManageAppProgressView.vue'), meta: { roles: STAFF, title: 'Setup progress' } },

  // ---------- Admin ----------
  { path: '/admin', name: 'admin', component: () => import(/* webpackChunkName: "admin" */ '../views/AdminDashboardView.vue'), meta: { roles: ADMIN, title: 'Admin dashboard' } },
  { path: '/admin/shops/:id', alias: '/admin/customers/:id', name: 'CustomerDetail', component: () => import(/* webpackChunkName: "admin" */ '../views/CustomerDetailView.vue'), props: true, meta: { roles: ADMIN, title: 'Shop' } },
  { path: '/admin/revenue-reports', name: 'admin-revenue-reports', component: () => import(/* webpackChunkName: "admin" */ '../views/AdminRevenueReportsView.vue'), meta: { roles: ADMIN, title: 'Revenue reports' } },
  { path: '/admin/templates', name: 'admin-templates', component: () => import(/* webpackChunkName: "admin" */ '../views/AdminTemplates.vue'), meta: { roles: ADMIN, title: 'Templates' } },
  { path: '/admin/products', name: 'AdminProducts', component: () => import(/* webpackChunkName: "admin" */ '../views/AdminProducts.vue'), meta: { roles: ADMIN, title: 'Products' } },
  { path: '/admin-settings', name: 'admin-settings', component: () => import(/* webpackChunkName: "admin" */ '../views/AdminSettingsView.vue'), meta: { roles: ADMIN, title: 'Admin settings' } },

  // ---------- Shop owners ----------
  { path: '/dashboard', name: 'dashboard', component: () => import(/* webpackChunkName: "customer" */ '../views/CustomerDashboardView.vue'), meta: { roles: CUSTOMER, title: 'Dashboard' } },
  { path: '/billing', name: 'billing', component: () => import(/* webpackChunkName: "customer" */ '../views/BillingView.vue'), meta: { roles: CUSTOMER, title: 'Billing' } },
  { path: '/customer-settings', name: 'customer-settings', component: () => import(/* webpackChunkName: "customer" */ '../views/CustomerSettingsView.vue'), meta: { roles: CUSTOMER, title: 'Settings' } },

  // ---------- Not found ----------
  { path: '*', name: 'not-found', component: () => import(/* webpackChunkName: "site" */ '../views/NotFoundView.vue'), meta: { title: 'Page not found' } }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  // New pages open at the top, Back returns to where you were, and links
  // to a section (#imports) land on it below the fixed header.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition
    if (to.path === from.path && to.hash !== from.hash) return false
    if (to.hash) return { selector: to.hash, offset: { x: 0, y: 96 } }
    return { x: 0, y: 0 }
  }
})

// ---------- Sign-in and role checks ----------

// Firebase restores the session asynchronously; wait for it once.
let authReady = null
function currentUser() {
  if (!authReady) {
    authReady = new Promise(resolve => {
      const stop = onAuthStateChanged(auth, user => {
        stop()
        resolve(user)
      })
    })
  }
  return authReady.then(() => auth.currentUser)
}

// One users/{uid} read per sign-in, not one per page.
let roleCache = { uid: null, role: null }
onAuthStateChanged(auth, user => {
  if (!user || user.uid !== roleCache.uid) roleCache = { uid: null, role: null }
})
async function currentRole(user) {
  if (roleCache.uid === user.uid && roleCache.role) return roleCache.role
  const snap = await getDoc(doc(db, 'users', user.uid))
  const role = snap.exists() ? snap.data().role || 'Customer' : 'Customer'
  roleCache = { uid: user.uid, role }
  return role
}

// Where each role lands after signing in (LoginView uses this too).
export function homeFor(role) {
  if (role === 'Admin') return '/admin'
  if (role === 'Staff') return '/staff-dashboard'
  return '/dashboard'
}

router.beforeEach(async (to, from, next) => {
  const roles = to.matched.reduce((acc, r) => r.meta.roles || acc, null)
  if (!roles) return next()

  const user = await currentUser()
  if (!user) {
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }

  try {
    const role = await currentRole(user)
    if (roles.includes(role)) return next()
    if (!['Admin', 'Staff', 'Customer'].includes(role)) {
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }
    // Signed in, but this page is for someone else: send them to their own home.
    return next(homeFor(role))
  } catch (e) {
    console.error('Could not check access:', e)
    return next({ path: '/login', query: { redirect: to.fullPath } })
  }
})

router.afterEach(to => {
  const t = to.meta && to.meta.title
  document.title = t ? `${t} · Salon Zap` : 'Salon Zap'
})

export default router